import { useState, useEffect, useCallback, useMemo,useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  fetchReportData,
  // exportReportData,
  // clearReportData,
  resetAppliedFilters,
  resetReportState
} from '../redux/ReportsSlice';

import { getReportConfig } from "../optionsConfig";

import {
  initialOutstandingFilters,
  initialDRGFilters,
  initialHACFilters,
  initialErrorDrgFilters,
  initialCodingKpisFilters,
  initialCoderKpisFilters,
  initialMissingMvdsFilters,
} from "../redux/ReportsSlice";

const INITIAL_FILTER_MAP = {
  "/outstanding-report": initialOutstandingFilters,
  "/drg-outlier-report": initialDRGFilters,
  "/hac-report": initialHACFilters,
  "/error-drg-report": initialErrorDrgFilters,
  "/coding-kpis-report": initialCodingKpisFilters,
  "/codingkpis-coder-report":initialCoderKpisFilters,
  "/missing-mvds-report": initialMissingMvdsFilters,
};



/* =========================================================
useReport — shared hook used by Outstanding, DRG, HAC report pages.

What it does:
  - Reads reportConfig (endpoint + method) from optionsConfig
  - Reads appliedFilters[reportPath] from Redux
  - Fetches report data on every filters/page/search/sort change
  - Returns all state + callbacks the parent component needs

Note: fetchModalFilterOptions has been removed — filter modals now
load their dropdown options via fetchColumnFilterOptions (same API
used by column-header filter badges).
========================================================= */

export const normalizeToArray = (data) => {
  if (!data) return [];

  // Already array
  if (Array.isArray(data)) return data;

  // Object → find first array inside
  if (typeof data === "object") {
    const firstArray = Object.values(data).find((v) => Array.isArray(v));
    return firstArray || [];
  }

  return [];
};

export const useReport = (reportPath,options = {}) => {
  const dispatch = useDispatch();
  const reportConfig = getReportConfig(reportPath);
  const { enabled = true } = options;         

  /* ── Local state ── */
  const [pageNumber, setPageNumber] = useState(1);
  // const [limit] = useState(10);
  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState("");
  const [sortPayload, setSortPayload] = useState({});
  const [totalPages, setTotalPages] = useState(1);
  const [resetSignal, setResetSignal] = useState(0);
  const debounceRef = useRef(null)
  const fetchDebounceRef = useRef(null);

  /* ── Redux state — all keyed by reportPath ── */
  const reportData = useSelector((s) => s.reports.reportData[reportPath] || {});
  const reportLoading = useSelector(
    (s) => s.reports.reportLoading[reportPath] || false,
  );
  const reportError = useSelector(
    (s) => s.reports.reportError[reportPath] || null,
  );
  const exportLoading = useSelector(
    (s) => s.reports.exportLoading[reportPath] || false,
  );
  const appliedFilters = useSelector(
    (s) => s.reports.appliedFilters[reportPath] || {},
  );

  /* ── Extract episodes (handles multiple API response shapes) ── */
  const episodes = useMemo(
    () =>
      reportData?.episodes || // Outstanding / HAC — body.episodes
      reportData?.episode_list || // common
      reportData?.data?.episode_list || // nested episode_list
      reportData?.data?.episodes || // nested episodes
      reportData?.data?.episode ||   // ← ADD THIS for Coding KPI
      (Array.isArray(reportData?.data) ? reportData.data : null) || // DRG: { total_episodes, data: [...] }
      [],
    [reportData],
  );

  useEffect(() => {
  let timeout;

  const updatePageSize = () => {
    clearTimeout(timeout);

    timeout = setTimeout(() => {
      const screenHeight = window.innerHeight;
      const reservedHeight = 335;
      const rowHeight = 36;

      const availableHeight =
        screenHeight - reservedHeight;

      const dynamicRows = Math.floor(
        availableHeight / rowHeight
      );

      const finalPageSize =
        dynamicRows > 10 ? dynamicRows : 10;

      setPageSize(prev =>
        prev !== finalPageSize
          ? finalPageSize
          : prev
      );
    }, 250);
  };

  updatePageSize();

  window.addEventListener(
    "resize",
    updatePageSize
  );

  return () => {
    clearTimeout(timeout);
    window.removeEventListener(
      "resize",
      updatePageSize
    );
  };
}, []);

  /* ── Compute total pages from various API pagination shapes ── */
  useEffect(() => {
     if (reportData?.total_pages) {
    setTotalPages(reportData.total_pages);
    return;
  }
    const total =
      reportData?.total_episodes || // DRG root-level total
      reportData?.no_of_episodes ||
      reportData?.data?.total_users_count ||   // ← ADD THIS for Coding KPI
      reportData?.pagination?.total_count ||
      reportData?.data?.pagination?.total_count ||
      reportData?.data?.pagination?.total_count_of_episode || (reportData?.total_pages)*10 ||
      0;

    if (total > 0) {
      setTotalPages(Math.ceil(total / pageSize));
    } else {
      // API doesn't return a total — enable next page only if we got a full page of results
      const count = episodes.length;
      setTotalPages(count >= pageSize ? pageNumber + 1 : pageNumber);
    }
  }, [reportData, episodes, pageSize, pageNumber]);

  /* ── Build the API payload ── */
  const buildPayload = useCallback(() => {
    const initialFilters = INITIAL_FILTER_MAP[reportPath] || {};

    const payload = {
      ...initialFilters,
      ...appliedFilters,
      page_number: pageNumber,
      limit:pageSize
    };

    if (searchText !== undefined) payload.search = searchText;

    if (sortPayload?.sort_by) {
      payload.sort_by = sortPayload.sort_by;
    }

    if (sortPayload?.sort_order) {
      payload.sort_order = sortPayload.sort_order;
    }

    return payload;
  }, [reportPath, appliedFilters, pageNumber, pageSize, searchText, sortPayload]);

  /* ── Fetch report data on any dependency change ── */
  useEffect(() => {
        if (!enabled) return;                          // ← ONLY change here
    if (!reportConfig?.reportEndpoint) return;
    dispatch(
      fetchReportData({
        reportPath,
        endpoint: reportConfig.reportEndpoint,
        method: reportConfig.method,
        payload: buildPayload(),
      }),
    );
  }, [
    enabled,     
    dispatch,
    reportPath,
    pageNumber,
    searchText,
    appliedFilters,
    sortPayload,
    pageSize
  ]);

  useEffect(() => {
    return () => {
      dispatch(resetReportState({ reportPath }));
    };
  }, [dispatch, reportPath]);
  // eslint-disable-line

  // useEffect(() => {
  //   //  Step 1: clear stale data so user doesn't see old rows
  //   dispatch(clearReportData({ reportPath }));

  //   //  Step 2: always fetch fresh — no "if data exists, skip" check
  //   dispatch(
  //     fetchReportData({
  //       reportPath,
  //       endpoint: reportConfig.endpoint,
  //       method: reportConfig.method,
  //       payload: appliedFilters,
  //     }),
  //   ).unwrap().then(()=>{
  //     dispatch(setRe)
  //   })
  // }, [reportPath]);

  /* ── Reset to page 1 on filter/search change ── */
  useEffect(() => {
    setPageNumber(1);
  }, [searchText, appliedFilters]);
  

  /* ── Callbacks ── */

  const handleSearchChange = useCallback((value) => {
    if (debounceRef.current){
      clearTimeout(debounceRef.current)
    }
    debounceRef.current = setTimeout(()=>{
      setSearchText(value)
    },500)
    // setSearchText(value);
    setPageNumber(1);
  }, []);

  const handleReset = useCallback(() => {
    dispatch(resetAppliedFilters({ reportPath }));
    setSearchText("");
    setSortPayload({});
    setPageNumber(1);
    setResetSignal((p) => p + 1);
  }, [dispatch, reportPath]);

  // const handleExport = useCallback(() => {
  //   if (!reportConfig?.exportEndpoint) return;
  //   const payload = buildPayload();
  //   delete payload.page_number;
  //   delete payload.limit;
  //   dispatch(
  //     exportReportData({
  //       reportPath,
  //       endpoint: reportConfig.exportEndpoint,
  //       method: reportConfig.method,
  //       payload,
  //     }),
  //   );
  // }, [dispatch, reportPath, reportConfig, buildPayload]);

  return {
    episodes,
    reportData,
    reportLoading,
    reportError,
    exportLoading,

    pageNumber,
    setPageNumber,
    totalPages,
     pageSize,
    setPageSize,
   searchText,
    setSearchText,
    handleSearchChange,
    sortPayload,
    setSortPayload,

    resetSignal,
    handleReset,
    // handleExport,
  };
};
